import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// Actions
import * as injuryActions from "../../state/injuries/actions";
import * as physicalDamageActions from "../../state/physicalDamage/actions";

// Config
import { capturePages, sensitiveSections } from "../../config/values";
import colors from "../../config/colors";
import isCaseLocked from "../../utils/isCaseLocked";

// Hooks
import useInjuries from "../../hooks/useInjuries";
import usePhysicalDamage from "../../hooks/usePhysicalDamage";

// Components
import EditMediaImageButtons from "./EditMediaImageButtons";
import ImageItem from "../ImageItem";
import ImageProcessing from "../ImageProcessing";

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
    margin: theme.spacing(0, 0.75),
    borderRadius: theme.shape.borderRadius,
    position: "relative",

    "&:last-child": {
      marginRight: "auto"
    }
  },
  imageItem: {
    backgroundColor: colors.whisper,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 152,
    height: 125,
    borderRadius: theme.shape.borderRadius
  }
}));

const EditMediaImage = ({
  handleDeleteImage,
  handleSelect,
  image,
  imageHeaders,
  imageRequest,
  index,
  isDeletingImage,
  loadingHeaders,
  page
}) => {
  const isInjury = page === capturePages.INJURY;
  const actions = isInjury ? injuryActions : physicalDamageActions;
  const { caseId, injuryId, physicalDamageId } = useParams();
  const injuryResult = useInjuries(caseId, injuryId, isInjury);
  const physicalDamageResult = usePhysicalDamage(
    caseId,
    physicalDamageId,
    !isInjury
  );
  const { data } = isInjury ? injuryResult : physicalDamageResult;
  const caseDetail = useSelector(s => s.caseDetail);
  const classes = useStyles();
  const dispatch = useDispatch();

  const updateImage = request => () => dispatch(actions.getImage(request));

  if (image.isProcessing) {
    return (
      <div className={classes.root}>
        <div className={classes.imageItem}>
          <ImageProcessing
            image={image}
            getImage={updateImage({
              ...imageRequest,
              filename: image.filename,
              index
            })}
          />
        </div>
      </div>
    );
  }

  const isLoading = isDeletingImage === image.filename;
  const onDelete = handleDeleteImage(image.filename);

  const isSensitive = isInjury && sensitiveSections.includes(data.bodySection);

  return (
    <div className={classes.root}>
      <ImageItem
        className={classes.imageItem}
        Component={
          !isCaseLocked(caseDetail.data) ? EditMediaImageButtons : null
        }
        componentProps={{ isLoading, onDelete }}
        image={image}
        imageHeaders={imageHeaders}
        imageRequest={imageRequest}
        isSensitive={isSensitive}
        loadingHeaders={loadingHeaders}
        onClick={handleSelect(index)}
      />
    </div>
  );
};

EditMediaImage.propTypes = {
  handleDeleteImage: PropTypes.func.isRequired,
  handleSelect: PropTypes.func.isRequired,
  image: PropTypes.shape({
    filename: PropTypes.string,
    isProcessing: PropTypes.bool
  }).isRequired,
  imageHeaders: PropTypes.shape({}).isRequired,
  imageRequest: PropTypes.shape({}).isRequired,
  index: PropTypes.number.isRequired,
  isDeletingImage: PropTypes.string,
  loadingHeaders: PropTypes.shape({}).isRequired,
  page: PropTypes.oneOf(Object.values(capturePages)).isRequired
};

EditMediaImage.defaultProps = {
  isDeletingImage: ""
};

export default EditMediaImage;
